import React, { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { VictoryAxis, VictoryBar, VictoryChart, VictoryStack, VictoryTooltip, VictoryZoomContainer } from 'victory';

import {
	border,
	dayEndMinute,
	dayStartMinute,
	defaultZoomState,
	fontFamily,
	hourAxisTicks,
	timelineChartLegendOptions,
	truncateLabel,
} from 'utils';
import {
	Button,
	ButtonLink,
	ButtonStack,
	ChartLegend,
	DateToggle,
	Drawer,
	ExportButton,
	FilterFields,
	LogoOverlay,
	PageHeading,
	Panel,
	PrintableReportHeader,
	Tooltip,
} from 'components';
import {
	RoomTurnoverResponse,
	ShortOption,
	TimelineEvent,
	useAppSelector,
	useGetRoomTurnoverQuery,
	useSystem,
} from 'store';
import { useFilters, useToast } from 'context';

import type { FlattenedTimelineData } from 'utils';

class CustomFlyout extends React.Component<{
	type: string;
	surgeon?: string;
	procedure?: string;
	x?: number;
	y?: number;
	datum?: { metadata: string; xName: string };
	delay_reason?: string;
}> {
	render() {
		const { x, y, datum, type, surgeon, procedure, delay_reason } = this.props;
		const isSchedule = datum?.xName?.includes('_schedule');
		return (
			<foreignObject x={x} y={y} width='100%' height='100%' className='overflow-visible'>
				<div className='bg-white flex flex-col drop-shadow-md w-20'>
					<div className='bg-white p-1 px-3 pl-1'>
						<p className='text-left text-[0.27em] font-semibold'>{datum?.xName?.replace('_schedule', '')}</p>
					</div>
					{type === 'fcots_delay' && (
						<>
							<div className='bg-blue-900 p-1 flex justify-between'>
								<p className='whitespace-nowrap text-left text-[0.25em] font-semibold text-white'>FCOTS Delay</p>
								<p className='text-right text-[0.25em] text-white'>{datum?.metadata}</p>
							</div>
							<div className='bg-blue-900 p-1 flex justify-between'>
								<p className='whitespace-nowrap text-left text-[0.25em] font-semibold text-white'>Delay Reason</p>
								<p className='text-right text-[0.25em] text-white'>{delay_reason ?? 'Unspecified'}</p>
							</div>
						</>
					)}

					{(type === 'case' || type === 'case_schedule' || type === 'wheels_in' || type === 'wheels_out') && (
						<>
							<div className='bg-blue-900 p-1 flex justify-between items-center'>
								<p className='whitespace-nowrap text-left text-[0.25em] font-semibold text-white w-3/4'>Surgeon</p>
								<p className='text-right text-[0.25em] text-white'>{surgeon}</p>
							</div>
							<div className='bg-blue-900 p-1 flex justify-between items-center'>
								<p className='whitespace-nowrap text-left text-[0.25em] font-semibold text-white w-3/4'>Procedure</p>
								<p className='text-right text-[0.25em] text-white'>{truncateLabel(procedure ?? '', 40)}</p>
							</div>
							<div className='bg-blue-900 p-1 flex justify-between'>
								<p className='whitespace-nowrap text-left text-[0.25em] font-semibold text-white'>
									{(type === 'wheels_in' && 'Wheels In') ||
										(type === 'wheels_out' && 'Wheels Out') ||
										(type === 'case' && 'Case Time') ||
										(type === 'case_schedule' && 'Case Time')}
								</p>
								<p className='text-right text-[0.25em] text-white'>{datum?.metadata}</p>
							</div>
						</>
					)}

					{type === 'turnover' && (
						<div className='bg-blue-900 p-1 flex justify-between items-center'>
							<p className='text-left text-[0.25em] font-semibold text-white w-1/2'>
								{isSchedule ? 'Estimated Turnover' : 'Turnover Under Target'}
							</p>
							<p className='text-right text-[0.25em] text-white'>{datum?.metadata}</p>
						</div>
					)}

					{type === 'turnover_warning' && (
						<div className='bg-blue-900 p-1 flex justify-between items-center'>
							<p className='text-left text-[0.25em] font-semibold text-white w-1/3'>Turnover Warning</p>
							<p className='text-right text-[0.25em] text-white'>{datum?.metadata}</p>
						</div>
					)}

					{type === 'case_addon' && (
						<>
							<div className='bg-blue-900 p-1 flex justify-between items-center'>
								<p className='whitespace-nowrap text-left text-[0.25em] font-semibold text-white w-3/4'>Surgeon</p>
								<p className='text-right text-[0.25em] text-white'>{surgeon}</p>
							</div>
							<div className='bg-blue-900 p-1 flex justify-between items-center'>
								<p className='whitespace-nowrap text-left text-[0.25em] font-semibold text-white w-3/4'>Procedure</p>
								<p className='text-right text-[0.25em] text-white'>{truncateLabel(procedure ?? '', 40)}</p>
							</div>
							<div className='bg-blue-900 p-1 flex justify-between items-center'>
								<p className='text-left text-[0.25em] font-semibold text-white'>Add-on</p>
								<p className='text-right text-[0.25em] text-white'>{datum?.metadata}</p>
							</div>
						</>
					)}
				</div>
			</foreignObject>
		);
	}
}

class CustomTicker extends React.Component<{
	x?: number;
	y?: number;
	text?: any;
	is_surgeon?: boolean;
	only_schedule_timelines?: boolean;
}> {
	render() {
		const { x, y, text, only_schedule_timelines } = this.props;
		const formatted_text = truncateLabel(only_schedule_timelines ? `${text}`.replace('_schedule', '') : `${text}`, 11);
		const xOffset = 30;
		return (
			<>
				{!text || only_schedule_timelines ? (
					<foreignObject x={(x ?? 0) - xOffset} y={(y ?? 0) - 8.5} width='100%' height='100%' className={`overflow-visible`}>
						<div className='bg-purple-50 whitespace-pre-line w-fit py-0.5 px-1 rounded-[0.2em] text-[0.3em] text-left text-black'>
							{only_schedule_timelines ? `${formatted_text}` : 'Scheduled'}
						</div>
					</foreignObject>
				) : (
					<foreignObject x={(x ?? 0) - xOffset} y={(y ?? 0) + 1} width='100%' height='100%' className={`overflow-visible`}>
						<div className='bg-gray-50 w-fit py-0.5 px-1 whitespace-pre-line rounded-[0.2em] text-[0.3em] text-left text-black'>
							{formatted_text}
						</div>
					</foreignObject>
				)}
			</>
		);
	}
}

interface TimelineProps {
	hideFilters?: boolean;
	hideDatePicker?: boolean;
	parentDate?: string;
	hideZoomPicker?: boolean;
	parentRooms?: ShortOption[];
	parentSurgeons?: string[];
	defaultProcedures?: string[];
	parentProvidedData?: RoomTurnoverResponse;
	truncateAxisLabel?: boolean;
	heightMultiple?: number;
	tickerTransform?: string;
	stackBarWidth?: number;
	maxRoomsDisplayed?: number;
	labelMaxLength?: number;
}

export function Timeline({
	hideFilters,
	hideDatePicker,
	parentDate,
	parentRooms,
	parentSurgeons,
	hideZoomPicker,
	heightMultiple = 22,
	tickerTransform = 'translate(0, 0)',
	labelMaxLength = 18,
	stackBarWidth = 13.5,
	maxRoomsDisplayed,
	truncateAxisLabel,
	defaultProcedures,
	parentProvidedData, // don't query endpoint, parent component will provide data
}: TimelineProps) {
	const { selectedSystem, selectedFacility } = useAppSelector((state) => state.userState);
	const { data: userData } = useSystem();
	const current_facility = userData?.facilities?.find((f) => f.id === selectedFacility);
	const system = userData?.healthsystems.find((h) => h.id === selectedSystem);
	const has_scheduled = current_facility?.has_scheduled ?? false;

	// flattened API response dataset for export
	const [csvDataset, setCsvDataset] = useState<FlattenedTimelineData[]>([]);
	const [printing, setPrinting] = useState(false);
	const [zoomState, setZoomState] = useState({ y: defaultZoomState });
	const zoomStateRange = Math.abs(zoomState.y[1] - zoomState.y[0]);

	// handle schedule display
	const [shownLines, setShownLines] = useState<string[]>(has_scheduled ? ['actual', 'schedule'] : ['actual']);

	const {
		date,
		fcotsGraceMinutes,
		wheelsTurnoverTimeThreshold,
		procTurnoverTimeThreshold,
		rooms,
		procedures,
		endDate,
		resetFilters,
		applyFilters,
		clearFilters,
		currentPageLoaded,
		filtersAreDirty,
		metadata,
		filtersAreFetching,
	} = useFilters();

	// There is sometimes a delay in our filters when a user switches pages
	// (which is why we check if currentPageLoaded is equal to our current page),
	// To account for the delay, we tell our RTK Query to skip until we set skipRequest to false.
	const [skipRequest, setSkipRequest] = useState(parentDate ? false : true);
	useEffect(() => {
		setTimeout(() => {
			if (currentPageLoaded === '/room-turnover') {
				setSkipRequest(false);
			}
		}, 0);
	}, [currentPageLoaded]);

	// eslint-disable-next-line prefer-const
	let { data, isFetching, error } = useGetRoomTurnoverQuery(
		{
			facility_id: selectedFacility,
			healthsystem_id: selectedSystem,
			filters: {
				date: parentDate
					? parentDate
					: date?.applied
							.toLocaleString('en-us', { year: 'numeric', month: '2-digit', day: '2-digit' })
							.replace(/(\d+)\/(\d+)\/(\d+)/, '$3-$1-$2'),
				wheels_turnover_threshold: wheelsTurnoverTimeThreshold?.applied,
				proc_turnover_threshold: procTurnoverTimeThreshold?.applied,
				fcots_grace_minutes: fcotsGraceMinutes?.applied,
				rooms: parentRooms ? parentRooms : rooms?.applied,
				surgeons: parentSurgeons,
				procedures: defaultProcedures ?? procedures?.applied.map((procedure) => procedure.name),
			},
			useCached: false,
			show_time_details: shownLines.includes('details'),
		},
		{
			skip: skipRequest || filtersAreFetching || !!parentProvidedData,
		}
	);

	if (parentProvidedData) {
		data = parentProvidedData;
		isFetching = false;
	}

	// Handle api response to create toast message on every error.
	const { createToast } = useToast();
	useEffect(() => {
		if (error) {
			if ('data' in error) {
				createToast({
					title: `${error.data}`,
					variant: 'error',
				});
			} else {
				createToast({
					title: `There was an error connecting to the server.`,
					variant: 'error',
				});
			}
		}
	}, [createToast, error]);
	// the charting library has a jagged render when bringing in new data, so we keep
	// the state below to show our loading indicator a few ms longer than its actually loading to cover that up
	const [delayedFetching, setDelayedFetching] = useState(isFetching);
	// set events to data from API or empty
	const arrayToSort = data?.events ?? [];
	const events = [...arrayToSort]
		.sort((a, b) => (a.data[0].x > b.data[0].x ? -1 : b.data[0].x > a.data[0].x ? 1 : 0))
		.filter((event) => {
			if (shownLines.includes('schedule') && shownLines.includes('actual')) {
				return true;
			} else if (shownLines.includes('schedule')) {
				return event.details?.room?.includes('_schedule');
			} else if (shownLines.includes('actual')) {
				return !event.details?.room?.includes('_schedule');
			}
		});

	// Groups all events by their x-value (which corresponds to the room in which
	// they took place), filtering out any "none" events.
	const eventsByX =
		events.reduce<Record<string, typeof events>>((groupedEvents, event) => {
			const {
				event_type,
				data: [{ x }],
			} = event;

			if (event_type === 'none') return groupedEvents;

			if (!Object.hasOwn(groupedEvents, x)) {
				groupedEvents[x] = [];
			}

			groupedEvents[x].push(event);

			return groupedEvents;
		}, {}) ?? {};

	const eventsByXBatches = [eventsByX];

	// when isFetching is switching to true, immediately show the loading indicator
	// when isFetching is switching to false, wait 250ms after done loading to hide the indicator
	useEffect(() => {
		if (isFetching) {
			setDelayedFetching(isFetching);
		} else {
			setTimeout(() => {
				setDelayedFetching(isFetching);
			}, 250);
		}
	}, [isFetching, setDelayedFetching]);

	// zoom in by 1 hour on either side of the chart
	const handleClickZoomIn = useCallback(() => {
		const [start, end] = zoomState.y;
		const newStart = Math.max(start + 60, 0);
		const newEnd = Math.min(end - 60, 1440);
		setZoomState({ y: [newStart, newEnd] });
	}, [zoomState, setZoomState]);

	// un-magnify by 1 hour on either side of the chart
	const handleClickZoomOut = useCallback(() => {
		const [start, end] = zoomState.y;
		let newStart = start - 60;
		let newEnd = end + 60;
		if (newStart <= 0) newStart = 0;
		if (newEnd >= 1440) newEnd = 1440;
		setZoomState({ y: [newStart, newEnd] });
	}, [zoomState, setZoomState]);

	// when we get new data from the API, create the downloadable file
	useEffect(() => {
		if (data?.events && data.events.length > 0 && csvDataset.length === 0) {
			// create a flat-map of the events, so that it's exportable to CSV
			const filteredEvents = data?.events.filter((e) => e.event_type !== 'none');
			setCsvDataset(
				filteredEvents.map(({ event_type, details, data: [firstDataItem] }) => ({
					event_type,
					...firstDataItem,
					...details,
				}))
			);
		}
	}, [data]);

	// reset to default state, same as on a page refresh
	const handleClickReset = useCallback(() => {
		setZoomState({ y: defaultZoomState });
	}, [setZoomState]);

	// determine the font sizes of the axis labels/ticks by checking the zoom-level and the window width
	let roomAxisLabelBaseFontSize: number;
	let timeAxisLabelBaseFontSize: number;

	if (zoomStateRange < 720) {
		roomAxisLabelBaseFontSize = 6;
		timeAxisLabelBaseFontSize = 6;
	} else if (zoomStateRange < 1200) {
		roomAxisLabelBaseFontSize = 6;
		timeAxisLabelBaseFontSize = 5;
	} else {
		roomAxisLabelBaseFontSize = 6;
		timeAxisLabelBaseFontSize = 4.5;
	}

	const roomAxisStyles = {
		axis: { stroke: border },
		tickLabels: {
			fontFamily: fontFamily,
			fontSize: printing ? roomAxisLabelBaseFontSize : roomAxisLabelBaseFontSize - 2,
			fill: '#000000',
			fontWeight: printing ? 600 : undefined,
			transform: tickerTransform,
		},
	};

	const timeAxisStyles = {
		axis: { stroke: border },
		grid: { stroke: border },
		ticks: { stroke: border, size: 5 },
		tickLabels: { fontFamily: fontFamily, fontSize: timeAxisLabelBaseFontSize, padding: 5 },
	};

	const minLimit = new Date(`${endDate.getFullYear() - 5}-Jan`);
	const arr = data?.events ? Array.from(new Set(data.events.map((value) => value.details.room ?? ''))) : [];
	const avg = arr && Math.round(arr.reduce((r, c) => r + c.length, 0) / arr.length);
	const regx = new RegExp('.{1,avg}'.replace('avg', avg > 20 ? avg.toString() : '8'), 'g');

	const updateShownLines = (option: string) => {
		if (!shownLines.includes(option)) {
			setShownLines([...shownLines, option]);
		} else {
			setShownLines(shownLines.filter((item) => item !== option));
		}
	};

	const only_schedule_timelines_shown = Object.keys(eventsByX).filter((key) => !key.includes('_schedule')).length === 0;
	const reportDate = parentDate ? new Date(`${parentDate}T00:00:00`) : date?.applied;
	const printableCaseDetails = events
		.filter((event) => ['case', 'case_schedule', 'case_addon'].includes(event.event_type))
		.map((event) => ({
			room: event.details.room?.replace('_schedule', '') ?? '',
			surgeon: event.surgeon ?? 'Unspecified',
			procedure: event.procedure ?? 'Unspecified',
			eventType:
				timelineChartLegendOptions.find((option) => option.type === event.event_type)?.label ??
				event.event_type.replaceAll('_', ' '),
			time: event.data[0]?.metadata ?? '',
		}));

	// Generate PDF from the current page, following the Analytics/Timely Starts export approach.
	const generatePDF = async () => {
		const element = document.getElementById('page-container');

		if (!element) {
			setPrinting(false);
			return;
		}

		try {
			const doc = new jsPDF();

			const html2canvasOptions = {
				useCORS: true,
				allowTaint: true,
				dpi: 200,
				scrollY: -window.scrollY,
			};

			const canvas = await html2canvas(element, html2canvasOptions);
			const imageData = canvas.toDataURL('image/jpeg');

			const pageWidth = doc.internal.pageSize.getWidth();
			const pageHeight = doc.internal.pageSize.getHeight();
			const margin = 10;
			const imageWidth = pageWidth - margin * 2;
			const imageHeight = (canvas.height * imageWidth) / canvas.width;
			const printablePageHeight = pageHeight - margin * 2;

			let heightLeft = imageHeight;
			let position = margin;

			doc.addImage(imageData, 'PNG', margin, position, imageWidth, imageHeight);
			heightLeft -= printablePageHeight;

			while (heightLeft > 0) {
				position = heightLeft - imageHeight + margin;
				doc.addPage();
				doc.addImage(imageData, 'PNG', margin, position, imageWidth, imageHeight);
				heightLeft -= printablePageHeight;
			}

			doc.save(`timeline_${current_facility?.name ?? 'report'}.pdf`);
		} catch (error) {
			console.error('Error generating PDF:', error); // keeping for easy error troubleshooting
		} finally {
			setPrinting(false);
		}
	};

	return (
		<div className='relative flex flex-col gap-10 overflow-hidden' id='page-container'>
			{printing && (
				<PrintableReportHeader>
					<p className='text-[2em] font-bold leading-tight text-bluegray-900'>Timeline View</p>
					<p className='mt-2 text-[1.35em] font-medium text-bluegray-500'>
						{reportDate ? format(reportDate, 'M/d/yyyy') : ''}
					</p>
					<p className='mt-1 text-[1.1em] font-medium text-bluegray-500'>{`${
						current_facility?.intraop_facility_name_alias
							? current_facility.intraop_facility_name_alias
							: current_facility?.name
					} - ${system?.name}`}</p>
				</PrintableReportHeader>
			)}
			{eventsByXBatches.map((eventsByXBatch, i) => {
				// Operations to get n unique rooms for this batch, helps with verticle height
				let distinctRooms = Array.from(
					new Set(
						Object.values(eventsByXBatch)
							.map((event) => event.map((event) => event.details.room))
							.reduce((accumulator, value) => accumulator.concat(value), [])
					)
				).length;

				if (maxRoomsDisplayed) {
					distinctRooms = Math.min(distinctRooms, maxRoomsDisplayed);
				}

				return (
					<div id={`graph-${i}`} key={i}>
						<div className='flex justify-between items-end mb-4'>
							{!hideFilters && !printing && <PageHeading className='mb-2'>Timeline View</PageHeading>}

							{!hideFilters && !printing && (
								<div className='flex justify-end'>
									<div className='mr-2'>
										{!hideDatePicker && (
											<DateToggle
												key={date?.applied.getTime()}
												selected={date?.applied}
												onApply={(d) => {
													date.update({
														applied: d,
														selected: d,
													});
												}}
												minLimit={minLimit}
											/>
										)}
									</div>
									<div className='mr-2'>
										<Tooltip content={'Actual Cases'}>
											<Button
												sizeX='square'
												sizeY='md'
												variant={shownLines.includes('actual') ? 'primary' : 'primary-ghost'}
												className='border-r-0 rounded-none'
												onClick={() => updateShownLines('actual')}
											>
												<span className='material-symbols-outlined'>history</span>
											</Button>
										</Tooltip>
										<Tooltip content={'Scheduled Cases'}>
											<Button
												sizeX='square'
												sizeY='md'
												disabled={!has_scheduled}
												variant={shownLines.includes('schedule') ? 'primary' : 'primary-ghost'}
												className='border-l-[0.01em] rounded-none'
												onClick={() => updateShownLines('schedule')}
											>
												<span className='material-symbols-outlined'>schedule</span>
											</Button>
										</Tooltip>
										<Tooltip content={'Show Time Detail'}>
											<Button
												sizeX='square'
												sizeY='md'
												variant={shownLines.includes('details') ? 'primary' : 'primary-ghost'}
												className='border-l-[0.01em] rounded-none'
												onClick={() => updateShownLines('details')}
											>
												<span className='material-symbols-outlined'>transition_fade</span>
											</Button>
										</Tooltip>
									</div>
									<Drawer
										metadata={metadata}
										filtersAreDirty={filtersAreDirty}
										trigger={
											<Button sizeX='sm' sizeY='md' variant={'primary-ghost'}>
												<span className='material-symbol'>filter_alt</span>
												Filters
											</Button>
										}
										quickActions={[
											{
												icon: 'undo',
												onClick: resetFilters,
												tooltipText: 'Discard unapplied filter changes',
												disabled: !filtersAreDirty,
											},
											{
												icon: 'restart_alt',
												onClick: clearFilters,
												tooltipText: 'Reset filters to default',
												disabled: !metadata.saved_at,
											},
										]}
										actionButtons={[
											{
												onClick: applyFilters,
												children: 'Apply',
												disabled: !filtersAreDirty,
											},
											{
												// eslint-disable-next-line @typescript-eslint/no-empty-function
												onClick: () => {},
												children: 'Views',
												disabled: false,
											},
										]}
									>
										<FilterFields fields={['date', 'fcotsGraceMinutes', 'wheelsTurnoverTimeThreshold', 'rooms', 'procedures']} />
									</Drawer>
									<div className='ml-2'>
										{events.length > 0 && (
											<ButtonStack>
												<Button sizeX='md' sizeY='md' variant='primary'>
													<ExportButton no_button={true} sizeX='md' sizeY='md' variant='primary' contents={csvDataset}>
														Export CSV
													</ExportButton>
												</Button>
												<Button
													sizeX='md'
													sizeY='md'
													variant='primary'
													onClick={() => {
														setPrinting(true);
														setTimeout(() => {
															generatePDF();
														}, 0);
													}}
												>
													Export PDF
												</Button>
											</ButtonStack>
										)}
									</div>
								</div>
							)}
						</div>

						<Panel
							headerContentCenter={
								!hideDatePicker &&
								!printing && (
									<DateToggle
										key={date?.applied.getTime()}
										selected={date?.applied}
										onApply={(d) => {
											date.update({
												applied: d,
												selected: d,
											});
											setCsvDataset([]);
										}}
										minLimit={minLimit}
									/>
								)
							}
							noHeader={true}
							isEmpty={data?.events.length === 0}
						>
							<div className='max-w-screen-2xl m-auto'>
								<div className='flex justify-end items-center py-4 '>
									<div className='flex items-center justify-end w-100'>
										<ChartLegend
											options={timelineChartLegendOptions
												.filter((o) => o.label !== 'none')
												.filter((o) =>
													!shownLines.includes('details') ? !['wheels_in', 'wheels_out'].includes(o.type ?? '') : true
												)}
										/>
									</div>
									{!(hideZoomPicker !== undefined) && !printing && (
										<div className='flex items-center justify-end ml-4 pl-4 border-blue-500 border-l whitespace-nowrap'>
											<ButtonLink className='mr-3' onClick={handleClickReset}>
												Reset Zoom
											</ButtonLink>
											<Button
												onClick={handleClickZoomOut}
												disabled={zoomStateRange >= 1440}
												sizeY='sm'
												sizeX='square'
												variant='primary-ghost'
												className='mr-2'
											>
												<span className='material-symbol'>zoom_out</span>
											</Button>
											<Button
												onClick={handleClickZoomIn}
												disabled={zoomStateRange <= 120}
												sizeY='sm'
												sizeX='square'
												variant='primary-ghost'
											>
												<span className='material-symbol'>zoom_in</span>
											</Button>
										</div>
									)}
								</div>
								<div className='cursor-grab relative pb-12'>
									{(delayedFetching || skipRequest || isFetching || filtersAreFetching) && !parentProvidedData && !printing && (
										<LogoOverlay backgroundColor='white' />
									)}
									<VictoryChart
										key={date?.applied.getTime()}
										domainPadding={10}
										horizontal
										height={distinctRooms > 0 ? heightMultiple * distinctRooms + 50 : 125}
										//width={550}
										domain={{ y: [dayStartMinute, dayEndMinute], x: [0, distinctRooms] }}
										padding={{
											top: 20,
											bottom: 0,
											right: 10,
											left: avg > 20 ? avg * 2.8 : Math.max(!parentSurgeons ? avg * 3.07 : 50, 40), // may need to change in future based on more testing
										}}
										containerComponent={
											<VictoryZoomContainer
												onZoomDomainChange={(domain) => {
													// domain is { x: DomainTuple, y: DomainTuple }
													// not quite sure how to convert that to numbers?
													const [start, end] = domain.y;
													if (typeof start === 'number' && typeof end === 'number') {
														const newStart = 60 * Math.ceil(start / 60.0);
														const newEnd = 60 * Math.ceil(end / 60.0);
														// TODO: it would probably be much more performant to do a debounce here, after like 50-100ms of delay
														// but didn't have time to do that when first implementing
														setZoomState({
															y: [newStart, newEnd],
														});
													}
												}}
												allowPan={true}
												allowZoom={false}
												zoomDomain={zoomState}
												zoomDimension='y'
											/>
										}
									>
										<VictoryAxis
											style={roomAxisStyles}
											tickValues={events.length === 0 ? ['Rooms'] : undefined}
											tickFormat={(t) =>
												t && (!t.toString().includes('_schedule') || only_schedule_timelines_shown)
													? truncateAxisLabel
														? truncateLabel(t, labelMaxLength)
														: t
													: ''
											}
											sortOrder='descending'
											tickLabelComponent={
												// eslint-disable-next-line prettier/prettier
												parentProvidedData || printing ? undefined : (
													<CustomTicker is_surgeon={!!parentSurgeons} only_schedule_timelines={only_schedule_timelines_shown} />
												)
											}
										/>

										<VictoryAxis
											label='Time'
											orientation='top'
											style={timeAxisStyles}
											dependentAxis
											tickValues={hourAxisTicks}
											tickFormat={(m: number) => `${m / 60}:00`}
										/>

										<VictoryAxis
											label=''
											orientation='top'
											style={{
												axis: { stroke: null, strokeDasharray: 2 },
												grid: { stroke: 'hsl(217 82% 15%)', strokeDasharray: 2 },
												ticks: { stroke: null, size: 10 },
												tickLabels: { fontFamily: fontFamily, fontSize: timeAxisLabelBaseFontSize, padding: 5 },
											}}
											dependentAxis
											tickValues={[data?.prime_time_start]}
											tickFormat={(m: number) => ``}
										/>

										<VictoryAxis
											label=''
											orientation='top'
											style={{
												axis: { stroke: null, strokeDasharray: 2 },
												grid: { stroke: 'hsl(217 82% 15%)', strokeDasharray: 2 },
												ticks: { stroke: null, size: 10 },
												tickLabels: { fontFamily: fontFamily, fontSize: timeAxisLabelBaseFontSize, padding: 5 },
											}}
											dependentAxis
											tickValues={[data?.prime_time_end]}
											tickFormat={(m: number) => ``}
										/>

										{Object.values(eventsByXBatch).map((eventGroup, i) => (
											<VictoryStack key={i}>
												{eventGroup.map((event, i) => (
													<VictoryBar
														key={i}
														alignment={event.details.room.includes('_schedule') ? 'start' : 'end'}
														barWidth={event.details.room.includes('_schedule') ? 8.0 : stackBarWidth}
														labels={() => ''}
														style={{
															data: {
																fill: fillColorPicker(eventGroup, event, i),
															},
															labels: {
																fontFamily,
															},
														}}
														data={
															event.details.room.includes('_schedule')
																? event.data.map((event) => ({
																		...event,
																		y: event.y - 2,
																  }))
																: event.data
														}
														labelComponent={
															<VictoryTooltip
																active={false}
																flyoutComponent={
																	<CustomFlyout
																		type={event.event_type}
																		surgeon={event.surgeon}
																		procedure={event.procedure}
																		delay_reason={event.details.reason ?? 'Unspecified'}
																	/>
																}
																orientation='left'
															/>
														}
													/>
												))}
											</VictoryStack>
										))}
									</VictoryChart>
								</div>
								{printing && printableCaseDetails.length > 0 && (
									<div className='mt-8 rounded-md border border-bluegray-100 bg-white p-6'>
										<p className='mb-4 text-h5 font-semibold text-bluegray-900'>Case Details</p>
										<table className='w-full border-collapse text-left'>
											<thead>
												<tr className='border-b border-bluegray-100'>
													<th className='p-2 text-p3 font-semibold uppercase text-bluegray-900'>Room</th>
													<th className='p-2 text-p3 font-semibold uppercase text-bluegray-900'>Surgeon</th>
													<th className='p-2 text-p3 font-semibold uppercase text-bluegray-900'>Procedure</th>
													<th className='p-2 text-p3 font-semibold uppercase text-bluegray-900'>Type</th>
													<th className='p-2 text-p3 font-semibold uppercase text-bluegray-900'>Time</th>
												</tr>
											</thead>
											<tbody>
												{printableCaseDetails.map((caseDetail, index) => (
													<tr
														key={`${caseDetail.room}-${caseDetail.surgeon}-${caseDetail.time}-${index}`}
														className='border-b border-bluegray-100'
													>
														<td className='p-2 text-p2 text-bluegray-900'>{caseDetail.room}</td>
														<td className='p-2 text-p2 text-bluegray-900'>{caseDetail.surgeon}</td>
														<td className='p-2 text-p2 text-bluegray-900'>{caseDetail.procedure}</td>
														<td className='p-2 text-p2 text-bluegray-900'>{caseDetail.eventType}</td>
														<td className='p-2 text-p2 text-bluegray-900'>{caseDetail.time}</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								)}
							</div>
							<div className='pt-12'></div>
						</Panel>
					</div>
				);
			})}
		</div>
	);
}

function fillColorPicker(eventGroup: TimelineEvent[], event: TimelineEvent, index: number) {
	return timelineChartLegendOptions.find((o) => o.type === event.event_type)?.color ?? 'bg-gray-100';
}

export default Timeline;
